/**
 * Per-form localStorage handle for the anonymous draft a filler has
 * started. We keep just enough to resume:
 *   - resultId   — the FormDocumentResult.id returned by startFormDraft
 *   - secretToken — UUID the server requires for anonymous save/submit
 *
 * Keyed by publicSlug so a user can hold drafts on multiple forms in
 * parallel.
 */
export interface DraftHandle {
  resultId: string;
  secretToken: string;
}

const PREFIX = 'form-draft:';

const isBrowser = typeof window !== 'undefined' && !!window.localStorage;

export function loadDraftHandle(publicSlug: string): DraftHandle | null {
  if (!isBrowser || !publicSlug) return null;
  try {
    const raw = window.localStorage.getItem(PREFIX + publicSlug);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (
      parsed &&
      typeof parsed.resultId === 'string' &&
      typeof parsed.secretToken === 'string'
    ) {
      return parsed;
    }
  } catch {
    // corrupt entry — fall through to null
  }
  return null;
}

export function saveDraftHandle(
  publicSlug: string,
  handle: DraftHandle,
): void {
  if (!isBrowser || !publicSlug) return;
  try {
    window.localStorage.setItem(PREFIX + publicSlug, JSON.stringify(handle));
  } catch {
    // Storage full / blocked — proceed without persistence; the user
    // simply won't be able to resume after a tab close.
  }
}

export function clearDraftHandle(publicSlug: string): void {
  if (!isBrowser || !publicSlug) return;
  try {
    window.localStorage.removeItem(PREFIX + publicSlug);
  } catch {
    // ignore
  }
}
